<script>
import Confirmation from "./confirmation";
import FormGroup from "@/components/molecules/form-group";
import FormInput from "@/components/molecules/form-input";
import Button from "@/components/atoms/button";
import Form from "@/components/molecules/form";
import FormSelect from "@/components/molecules/form-select";
import FormSelectOption from "@/components/molecules/form-select/form-select-option";
import Alert from "@/components/molecules/alert";
import StateOverlay from "@/components/molecules/state-overlay";
import {
  formValidationProps,
  useFormValidation
} from "@/components/organisms/mixins/form-validation.mixin";
import Divider from "@/components/atoms/divider";
import FormFieldset from "@/components/molecules/form-fieldset";
import { useInteractionDoneTracking } from "@/mixins/tracking/interaction-done";

export default {
  name: "PanierToListeForm",
  components: {
    Form,
    StateOverlay,
    FormGroup,
    FormInput,
    FormSelect,
    FormSelectOption,
    Button,
    Alert,
    Confirmation,
    Divider,
    FormFieldset
  },
  props: {
    error: {
      type: Object,
      required: false,
      default: null
    },
    isListesLoading: {
      type: Boolean,
      required: true
    },
    listes: {
      type: Array,
      required: false,
      default: () => {
        return [];
      }
    },
    ...formValidationProps()
  },
  emits: ["saveNewListe", "editListe"],
  computed: {
    title() {
      if (this.titre !== "") {
        return this.titre;
      } else if (this.liste) {
        const liste = this.listes.find((ldc) => ldc.id === this.liste);
        return liste.attributes.titre;
      }
      return "";
    }
  },
  setup(props) {
    const { trackEvent } = useInteractionDoneTracking();
    const {
      v$,
      getState,
      getErrorMessage,
      scrollToFirstError,
      isSuccess,
      isLoadingComputed
    } = useFormValidation(props);

    return {
      trackEvent,
      v$,
      getState,
      getErrorMessage,
      scrollToFirstError,
      isSuccess,
      isLoadingComputed
    };
  },
  data() {
    return {
      titre: "",
      liste: null
    };
  },
  validations() {
    return {
      titre: {
        listeOrTitreRequired() {
          return this.liste || this.titre;
        }
      }
    };
  },
  methods: {
    saveListe() {
      if (!this.v$.$invalid) {
        if (this.titre) {
          this.trackEvent({
            actionGA: trackingCodes.ACTIONGA.createShoppingList,
            labelGA: "panier",
            categoryGA: trackingCodes.SHOPPINGLIST
          });
          this.$emit("saveNewListe", this.titre);
        } else {
          this.$emit("editListe", this.liste);
        }
      } else {
        this.v$.$touch();
        this.scrollToFirstError();
      }
    }
  }
};
</script>

<template>
  <StateOverlay
    overlay
    :is-loading="isLoadingComputed || isListesLoading"
    :message="isLoadingComputed ? 'Enregistrement de la liste de courses' : 'Chargement de vos listes de courses'"
  >
    <Form
      v-if="!isSuccess"
      :error="error"
      @submit.prevent="saveListe()"
    >
      <h2 class="u-font-24 u-color-primary u-mb-xxs u-text-center">
        Enregistrez mon panier en liste de courses
      </h2>

      <Divider
        dotted
        class="u-mt-lg"
      />

      <div class="u-flex u-flex-justify-between u-flex-column@sm u-flex-align-center@sm u-mt-md u-ml-lg u-mr-lg u-mb-md">
        <FormGroup
          v-if="listes && listes.length > 0"
          class="u-flex-item-fluid"
        >
          <label for="choisirLdc">
            <p class="u-font-700 u-mb-xs">
              Sélectionner une liste existante
            </p>
          </label>
          <FormSelect
            id="choisirLdc"
            v-model="liste"
            size="md"
            default-value="Choisir une liste"
          >
            <FormSelectOption
              v-for="liste in listes"
              :key="liste.id"
              :value="liste.id"
            >
              {{ liste.attributes.titre }}
            </FormSelectOption>
          </FormSelect>
        </FormGroup>
        <p
          v-if="listes && listes.length > 0"
          class="u-ml-lg u-mr-lg u-mt-lg u-mb-lg@sm u-pt-xs u-text-center@sm"
        >
          OU
        </p>
        <FormFieldset
          v-if="!listes || listes.length === 0"
          size="md"
          center
        >
          <FormGroup
            :state="getState(v$,'titre')"
            class="u-flex-item-fluid"
          >
            <p class="u-font-700 u-mb-xs">
              Créer une nouvelle liste
            </p>
            <FormInput
              v-model="titre"
              class="u-mb-xs"
              size="md"
              :state="getState(v$,'titre')"
              @input="liste = null"
            >
              Titre de la liste
            </FormInput>
            <template #message>
              <p>
                {{ getErrorMessage("titre","Merci de renseigner un titre") }}
              </p>
            </template>
          </FormGroup>
        </FormFieldset>
        <FormGroup
          v-else
          :state="getState(v$,'titre')"
          class="u-flex-item-fluid"
        >
          <p class="u-font-700 u-mb-xs">
            Créer une nouvelle liste
          </p>
          <FormInput
            v-model="titre"
            class="u-mb-xs"
            size="md"
            :state="getState(v$,'titre')"
            @input="liste = null"
          >
            Titre de la liste
          </FormInput>
          <template #message>
            <p>
              {{ getErrorMessage("titre","Merci de renseigner un titre") }}
            </p>
          </template>
        </FormGroup>
      </div>

      <div class="u-text-center u-mt-lg u-mb-lg">
        <Button
          radius="full"
          variant="secondary"
          size="md"
          :disabled="!liste && !titre"
          type="submit"
          value="J’enregistre mes produits en liste"
        />
      </div>
      <div
        v-if="listes && listes.length > 0"
        class="u-text-center"
      >
        <Alert
          has-icon
          icon-position="left"
          icon-name="info-circle"
          icon-size="sm"
          icon-color="primary-light"
          radius="md"
          class="u-ml-sm u-mr-sm u-mb-sm u-flex-item-fluid"
        >
          Si vous sélectionnez une liste existante,
          le contenu actuel de celle-ci sera écrasé
        </Alert>
      </div>
    </Form>
    <Confirmation
      v-else
      :titre="title"
    />
  </StateOverlay>
</template>
